import { Injectable } from '@angular/core';
import { BackgroundGeolocationPlugin, CallbackError, Location } from "@capacitor-community/background-geolocation";
import { distinctUntilKeyChanged } from 'rxjs/operators';
import { AuthAction } from 'src/app/core/authentication/auth-action';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { AccountService } from './account.service';
import { registerPlugin, CapacitorHttp } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { FirebaseCrashlytics } from '@capacitor-community/firebase-crashlytics';
import { LocalNotifications } from '@capacitor/local-notifications';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>("BackgroundGeolocation");

@Injectable({
    providedIn: 'root'
})
export class GeolocationService {
    private watcherId?: string;

    constructor(
        private authService: AuthService,
        private accountService: AccountService,
    ) { }

    async initGeolocation(): Promise<void> {
        this.accountService.account$
            .pipe(distinctUntilKeyChanged('id'))
            .subscribe(() => this.configureBackgroundGeolocation());

        await this.handleNotificationPermission();
        this.authService.onAuthChanged.subscribe((authAction) => {
            if (authAction === AuthAction.Logout) {
                if (this.watcherId) {
                    BackgroundGeolocation.removeWatcher({ id: this.watcherId });
                }
            }
        })
    }

    private configureBackgroundGeolocation(): void {
        if (this.watcherId) BackgroundGeolocation.removeWatcher({ id: this.watcherId });

        BackgroundGeolocation.addWatcher({
            backgroundMessage: 'Background tracking enabled',
            stale: true,
            distanceFilter: 150,
        }, (location, error) => {
            if (error) {
                this.handleCallbackError(error);
            } else {
                this.postLocation(location);
            }
        }).then((watcherId) => {
            this.watcherId = watcherId;
        });
    }

    async handleNotificationPermission() {
        const permissions = await LocalNotifications.checkPermissions();

        if (permissions.display === 'granted') {
            console.log('Notification permission already granted');
        } else {
            console.log('Notification permission not requested yet');
            const { display } = await LocalNotifications.requestPermissions();

            if (display !== 'granted') {
                const confirmOpenSettings = window.confirm(
                    "This app uses notifications but does not have permission.\n\n" +
                    "Would you like to open the settings to enable notifications?"
                );

                if (confirmOpenSettings) {
                    try {
                        await NativeSettings.open({
                            optionAndroid: AndroidSettings.AppNotification,
                            optionIOS: IOSSettings.App
                        });
                    } catch (error) {
                        console.error('Failed to open settings:', error);
                    }
                }
            }
        }
    }

    private postLocation(location: Location | undefined): void {
        if (!location) return;
        CapacitorHttp.post({
            url: `${environment.apiBaseUrl}/courier/set-position`,
            headers: {
                'X-AUTH-TOKEN': this.authService.getAuthorizationToken()!,
                'X-API-KEY': environment.apiKey,
                'Content-Type': 'application/json'
            },
            data: {
                positions: [
                    {
                        id: location.time?.toString(),
                        latitude: location.latitude.toString(),
                        longitude: location.longitude.toString(),
                        time: location.time?.toString(),
                    }
                ]
            }
        }).then((response) => {
            console.log('response', response);
        }).catch((error) => {
            if (!environment.production) {
                console.error(error);
            }

            FirebaseCrashlytics.recordException({ message: JSON.stringify(error, null, 2) });
        });
    }

    private handleCallbackError(error: CallbackError): void {
        if (!environment.production) console.error(error);

        if (error.code === "NOT_AUTHORIZED") {
            if (window.confirm(
                "This app needs your location, " +
                "but does not have permission.\n\n" +
                "Open settings now"
            )) {
                BackgroundGeolocation.openSettings();
            }
        } else {
            FirebaseCrashlytics.recordException({ message: JSON.stringify(error, null, 2) });
        }
    }
}